import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { isMeasurementField, validateMeasurement, type MeasurementField } from '@lensmira/domain'

import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { FrameChoice, frameName, type FrameOption } from './FrameChoice.js'

/**
 * Taking the measurements a lens is made to. §6.4 `RX-006`, `RX-007`; §6.8 `DSP-002`; Appendix I.
 *
 * **Nothing in the product recorded one.** `fitting_measurement` was read by the customer record
 * and counted by erasure, and written by no service, route or screen, so `CUS-002`'s "fitting
 * measurements per frame owned" was a permanently empty section and `SendToLab` sent whatever was
 * typed onto that order rather than what was measured off this customer's face.
 *
 * **On the customer rather than on the order line**, which is where the frame is chosen. `RX-006`
 * says the measurements belong to the frame and `SendToLab` reads the newest fitting for the
 * customer, so taking them here is what makes a lab order carry them: the dispenser is standing in
 * front of the person with the frame on their face, which is not the moment they are typing a
 * price.
 *
 * **Every field is optional and the form refuses an empty one.** A fitting is taken in pieces: a PD
 * at the first visit, a segment height once the frame is picked. A form that demanded all fourteen
 * would be a form nobody completes, and a row of nulls records nothing.
 *
 * **The bounds are the server's and the warnings come back from it.** Appendix I's outer bound
 * blocks and its inner band warns (`decisions/0004`), and both live in `packages/domain`. The
 * screen checks nothing numeric itself: a second copy of 22 to 40 here would be the one that
 * drifted, and what it would save is a round trip on a value somebody mistyped.
 */

/** One measurement, as the form collects it: everything is text until it is sent. */
interface FieldSpec {
  readonly key: string
  readonly label: MessageKey
  /**
   * The Appendix I rule that bounds this field, where it has one.
   *
   * **The numbers are not here.** `validateMeasurement` holds them, and the counter asks the same
   * function the server asks. A copy of "22 to 40" in this file would be the one that drifted, and
   * the point is not to re-decide the bound but to stop a mistyped one reaching the queue: a
   * refused write holds the head of a serial queue (`OFF-001a`), and a PD typed as 4 is a typo
   * rather than a decision.
   *
   * Absent means the appendix states no bound, which is not the same as nobody checking: near PD
   * has no rule of its own and `fittings.service.ts` records why.
   */
  readonly rule?: MeasurementField
}

/**
 * The fields, in the order a dispenser takes them: the face, then the frame on it.
 *
 * Grouped rather than flat, because fourteen boxes in a column is a form somebody skips the bottom
 * of, and the bottom is the frame geometry a lab needs to edge to.
 */
const FACE: readonly FieldSpec[] = [
  { key: 'monocularPdOdMm', label: 'fittings.pdOd', rule: 'monocularPd' },
  { key: 'monocularPdOsMm', label: 'fittings.pdOs', rule: 'monocularPd' },
  { key: 'nearPdMm', label: 'fittings.nearPd' },
  { key: 'segmentHeightOdMm', label: 'fittings.segOd', rule: 'segmentHeight' },
  { key: 'segmentHeightOsMm', label: 'fittings.segOs', rule: 'segmentHeight' },
  { key: 'fittingHeightMm', label: 'fittings.fittingHeight' },
]

const FRAME: readonly FieldSpec[] = [
  { key: 'vertexDistanceMm', label: 'fittings.vertex', rule: 'vertexDistance' },
  { key: 'pantoscopicTiltDeg', label: 'fittings.pantoscopic' },
  { key: 'faceFormWrapDeg', label: 'fittings.wrap' },
  // `frame_a_mm` is the eye size Appendix I calls `frame_eye_size`, and `frame_dbl_mm` is the
  // bridge. The service maps them the same way and says so.
  { key: 'frameAMm', label: 'fittings.frameA', rule: 'frameEyeSize' },
  { key: 'frameBMm', label: 'fittings.frameB' },
  { key: 'frameDblMm', label: 'fittings.frameDbl', rule: 'bridge' },
  { key: 'frameEdMm', label: 'fittings.frameEd' },
  { key: 'templeLengthMm', label: 'fittings.temple', rule: 'temple' },
]

const ALL = [...FACE, ...FRAME]

export interface FittingsProps {
  readonly customerId: string
  /** `DSP-002` takes these at dispense, which is Appendix B's `dispense_create`. */
  readonly canMeasure: boolean
  /** The frames this branch can sell, so the frame is chosen rather than typed. */
  readonly loadFrames?: (query: string) => Promise<readonly FrameOption[]>
  readonly onRecorded?: () => void
}

export function Fittings({
  customerId,
  canMeasure,
  loadFrames,
  onRecorded,
}: FittingsProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [values, setValues] = useState<Readonly<Record<string, string>>>({})
  const [frame, setFrame] = useState<FrameOption | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<readonly string[]>([])
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    const answer = (
      typeof write.response === 'object' && write.response !== null ? write.response : {}
    ) as { created?: { warnings?: unknown } }
    const given = Array.isArray(answer.created?.warnings) ? answer.created.warnings : []
    // Appendix I's inner band, shown and not hidden: the measurement was stored and somebody should
    // look at it again. `decisions/0004` makes this a warning rather than a refusal.
    setWarnings(
      given
        .map((row) => (row as { message?: unknown })?.message)
        .filter((message): message is string => typeof message === 'string'),
    )
    setValues({})
    setFrame(null)
    setDone(true)
    onRecorded?.()
  })

  const record = useCallback(async () => {
    const numbers: Record<string, number> = {}
    for (const field of ALL) {
      const text = (values[field.key] ?? '').trim()
      if (text === '') continue
      const value = Number(text)
      if (!Number.isFinite(value)) {
        // The one numeric check here, and it is about the keyboard rather than about Appendix I: a
        // value that is not a number at all cannot be bounded, and the server's refusal for it
        // would stop the whole write queue.
        setError(t('fittings.notANumber', { field: t(field.label) }))
        return
      }
      /**
       * Appendix I's blocking bound, asked of the same function the server asks.
       *
       * Found in a browser walkthrough: a PD typed as 4 reached the server, was refused with the
       * right sentence, and stopped the write queue with everything behind it. There is a Discard
       * on a blocked write, so it is not a wedged till, and a typo should not get that far.
       *
       * Only the blocking half. The warn band comes back from the server with the answer and is
       * shown there, which is `decisions/0004`'s two tiers: 23mm is unusual and is somebody's face.
       */
      if (field.rule !== undefined && isMeasurementField(field.rule)) {
        const blocking = validateMeasurement(field.rule, value).find((f) => f.severity === 'block')
        if (blocking) {
          setError(`${t(field.label)}: ${blocking.message}`)
          return
        }
      }
      numbers[field.key] = value
    }
    if (Object.keys(numbers).length === 0) {
      setError(t('fittings.nothingTaken'))
      return
    }

    setError(null)
    setDone(false)
    setWarnings([])
    const write = (await enqueue({
      method: 'POST',
      path: `/customers/${encodeURIComponent(customerId)}/fittings`,
      label: t('fittings.queueLabel'),
      body: {
        ...numbers,
        // `RX-006`: they belong to the frame, and absent while one is still being chosen.
        ...(frame === null ? {} : { frameVariantId: frame.frameVariantId }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [customerId, enqueue, frame, t, values])

  if (!canMeasure) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.fitting'))

  if (!open) {
    return (
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setOpen(true)
          setDone(false)
        }}
      >
        {t('fittings.start')}
      </button>
    )
  }

  const field = (spec: FieldSpec): JSX.Element => (
    <label className="lm-field lm-field--inline" key={spec.key}>
      <span className="lm-field__label">{t(spec.label)}</span>
      <input
        className="lm-input"
        inputMode="decimal"
        autoComplete="off"
        value={values[spec.key] ?? ''}
        onChange={(e) => {
          const typed = e.currentTarget.value
          setValues((previous) => ({ ...previous, [spec.key]: typed }))
        }}
      />
    </label>
  )

  return (
    <section className="lm-fittings" aria-labelledby="fittings-heading">
      <h2 id="fittings-heading" className="lm-fittings__heading">
        {t('fittings.heading')}
      </h2>
      <p className="lm-fittings__effects">{t('fittings.means')}</p>

      {loadFrames === undefined ? null : (
        <>
          <FrameChoice
            load={loadFrames}
            {...(frame === null ? {} : { chosenStockItemId: frame.stockItemId })}
            onChoose={setFrame}
          />
          <p className="lm-hint">
            {frame === null
              ? t('fittings.noFrameYet')
              : t('fittings.againstFrame', { frame: frameName(frame) })}
          </p>
        </>
      )}

      <h3 className="lm-fittings__subheading">{t('fittings.faceHeading')}</h3>
      <div className="lm-fittings__grid">{FACE.map(field)}</div>

      <h3 className="lm-fittings__subheading">{t('fittings.frameHeading')}</h3>
      <div className="lm-fittings__grid">{FRAME.map(field)}</div>

      {error === null ? null : <p className="lm-error">{error}</p>}

      {warnings.length === 0 ? null : (
        <>
          <p className="lm-fittings__effects">{t('fittings.warned')}</p>
          <ul className="lm-fittings__warnings">
            {warnings.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </>
      )}
      {done && warnings.length === 0 ? (
        <p className="lm-fittings__effects">{t('fittings.recorded')}</p>
      ) : null}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void record()
        }}
      >
        {t('fittings.record')}
      </button>
    </section>
  )
}
